// NavigationGuardContext.js - Global navigation guard for unsaved changes
import React, { createContext, useContext, useState, useCallback } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

const NavigationGuardContext = createContext(null);

export const useNavigationGuard = () => {
  const context = useContext(NavigationGuardContext);
  if (!context) {
    throw new Error('useNavigationGuard must be used within a NavigationGuardProvider');
  }
  return context;
};

export const NavigationGuardProvider = ({ children }) => {
  const [guardState, setGuardState] = useState({
    isDirty: false,
    message: 'You have unsaved changes. Are you sure you want to leave?',
    title: 'Unsaved Changes',
    onConfirm: null,
  });
  const [showDialog, setShowDialog] = useState(false);
  const [pendingCallback, setPendingCallback] = useState(null);
  const [, setPendingPath] = useState(null);

  // Set the dirty state for the current form/page
  const setDirty = useCallback((isDirty, options = {}) => {
    setGuardState(prev => ({
      ...prev,
      isDirty,
      message: options.message || prev.message,
      title: options.title || prev.title,
    }));
  }, []);

  // Register a guard with custom confirmation handler
  const registerGuard = useCallback((options = {}) => {
    setGuardState({
      isDirty: true,
      message: options.message || 'You have unsaved changes. Are you sure you want to leave?',
      title: options.title || 'Unsaved Changes',
      onConfirm: options.onConfirm || null,
    });
  }, []);

  // Unregister the guard (when leaving the page safely)
  const unregisterGuard = useCallback(() => {
    setGuardState({
      isDirty: false,
      message: 'You have unsaved changes. Are you sure you want to leave?',
      title: 'Unsaved Changes',
      onConfirm: null,
    });
  }, []);

  // Check if navigation should be blocked
  const checkNavigation = useCallback((callback, path) => {
    if (guardState.isDirty) {
      setPendingCallback(() => callback);
      setPendingPath(path);
      setShowDialog(true);
      return false;
    }
    return true;
  }, [guardState.isDirty]);

  // Handle confirm navigation
  const handleConfirm = () => {
    setShowDialog(false);
    if (pendingCallback) {
      pendingCallback();
    }
    // Clear the guard after confirming
    unregisterGuard();
  };

  // Handle cancel navigation
  const handleCancel = () => {
    setShowDialog(false);
    setPendingCallback(null);
    setPendingPath(null);
  };

  const value = {
    setDirty,
    registerGuard,
    unregisterGuard,
    checkNavigation,
    isDirty: guardState.isDirty,
  };

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={showDialog}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={guardState.title}
        message={`${guardState.message}\n\nAny unsaved changes will be lost.`}
        severity="warning"
        confirmText="Leave Page"
        confirmColor="warning"
        cancelText="Stay on Page"
      />
    </NavigationGuardContext.Provider>
  );
};

export default NavigationGuardContext;
